import { useState } from "react";

import type { Citation } from "@/types/eryndal";
import { cn } from "@/utils/cn";

interface Props {
  citations: Citation[];
  className?: string;
}

/**
 * The citations rail. Collapsed by default — the numbered chips under the
 * answer already carry the glanceable information, so a long source list
 * sitting open just pushes the reader away from the answer.
 *
 * Uses the same native <details>/<summary> disclosure as ThinkingPanel rather
 * than pulling in a headless disclosure library for one component.
 */
export function CitationsPanel({ citations, className }: Props) {
  const [open, setOpen] = useState(false);

  if (!citations.length) return null;

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className={cn("panel-atelier h-fit overflow-hidden", className)}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-[17px] py-3 transition-colors duration-200 ease-atelier hover:bg-parchment-100">
        <span className="label-mono">
          Citations · {citations.length}
        </span>
        <span
          className={cn(
            "font-mono text-[0.7rem] text-warmgray-400 transition-transform duration-300 ease-atelier",
            open && "rotate-180",
          )}
        >
          ▾
        </span>
      </summary>

      {/* Same treatment as ThinkingPanel's body: capped and scrolled rather
          than expanding to whatever the source list happens to be. A turn
          with two dozen citations used to unroll the entire rail. */}
      <ol
        data-testid="citation-entries"
        className="animate-ery-rise max-h-[40vh] space-y-1 overflow-y-auto border-t border-ruleline px-[17px] py-3 font-serif text-sm text-warmgray-500"
      >
        {citations.map((c, idx) => (
          <li
            key={c.id ?? c.ref ?? idx}
            className="flex gap-2.5 rounded-chip p-1.5 transition-colors duration-200 ease-atelier hover:bg-parchment-100"
          >
            <span className="font-mono text-xs text-oxblood-400">[{idx + 1}]</span>
            <div className="min-w-0 flex-1">
              <div className="text-espresso-700">{c.title ?? c.ref ?? c.id ?? "Unnamed source"}</div>
              {c.snippet ? <div className="mt-1 italic text-warmgray-400">“{c.snippet}”</div> : null}
              {c.url ? (
                <a
                  className="mt-1 block truncate text-xs text-oxblood-500 underline decoration-oxblood-200 underline-offset-2 hover:text-oxblood-600"
                  href={c.url}
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  {c.url}
                </a>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}
